"use client";
import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import {
  GemsBadge,
  PerpetualRewardsBadge,
  PointsBadge,
  WeeklyPointsBadge,
} from "@/app/games/(details)/quest-badges";
import { type GameData, getQuestIcon } from "@/app/games/(details)/types";
import type {
  CampaignQuest,
  CampaignQuestState,
} from "@/components/QuestsV2/types";
import { formatNumber } from "@/components/ui/custom/leaderboard-v2";
import { ListItem, listItemTextVariants } from "@/components/ui/list";
import { Progress } from "@/components/ui/progress";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import { cn } from "@/lib/utils";
import { IterationCcw } from "lucide-react";
import { Fragment, useCallback, useMemo } from "react";

export const Quests = ({
  gameData,
  className,
}: { gameData: GameData; className?: string }) => {
  const { track } = useGamePageAnalytics();
  const {
    allQuests,
    completeQuest,
    inProgressQuest,
    setInProgressQuest,
    onQuestClick,
  } = useGameDetailsContext();

  const totalQuesters = useMemo(() => {
    return (
      allQuests?.find((quest) => quest.type === "wishlist")?.participantCount ??
      0
    );
  }, [allQuests]);

  // Calculate progress based on total rewards earned vs total possible gems
  const { totalGemsEarned, totalPossibleGems, progressPercentage } =
    useMemo(() => {
      const totalGemsEarned =
        allQuests?.reduce((sum, quest) => {
          // For completed quests, multiply gems by completion count (for recurring quests)
          if (
            quest.completed &&
            quest.completionCount &&
            quest.completionCount > 0
          ) {
            return sum + quest.gems * quest.completionCount;
          }
          return sum;
        }, 0) ?? 0;

      const totalPossibleGems =
        allQuests?.reduce((sum, quest) => sum + quest.gems, 0) ?? 0;

      // Ensure progress doesn't exceed 100% for recurring quests
      const progressPercentage =
        totalPossibleGems > 0
          ? Math.min((totalGemsEarned / totalPossibleGems) * 100, 100)
          : 0;

      return { totalGemsEarned, totalPossibleGems, progressPercentage };
    }, [allQuests]);

  const handleQuestClick = useCallback(
    (quest: CampaignQuestState) => {
      if (quest.completedToday) return;

      track({
        action: Action.PRESSED,
        extras: {
          control: "quest",
          controlLabel: quest.name,
          controlType: ControlType.LIST_ITEM,
          section: GamePageSection.RIGHT_PANEL,
        },
      });

      onQuestClick(quest);
    },
    [track, onQuestClick],
  );

  const handleQuestComplete = useCallback(
    async (quest: CampaignQuest) => {
      const questCompleted = await completeQuest(quest);

      if (questCompleted) {
        track({
          action: Action.SUCCEEDED,
          extras: {
            control: "quest",
            controlLabel: quest.name,
            controlType: ControlType.LIST_ITEM,
            section: GamePageSection.RIGHT_PANEL,
          },
        });

        return;
      }

      track({
        action: Action.FAILED,
        extras: {
          control: "quest",
          controlLabel: quest.name,
          controlType: ControlType.LIST_ITEM,
          section: GamePageSection.RIGHT_PANEL,
        },
      });
    },
    [completeQuest, track],
  );

  return (
    <div className={cn("font-suisse w-full mt-8", className)}>
      <h1 className="font-semibold text-2xl leading-8 text-neutral-50 text-center">
        {gameData.details.name} Quests
      </h1>

      {totalQuesters > 0 ? (
        <p className="font-suisse text-sm text-muted-foreground">
          {formatNumber(totalQuesters)} participants
        </p>
      ) : null}

      <div className="flex items-center gap-5 mt-4">
        <Progress
          value={progressPercentage}
          className="bg-neutral-50/20 flex-1"
        />
        <GemsBadge
          text={`${totalGemsEarned} / ${Math.max(totalPossibleGems ?? 0, totalGemsEarned ?? 0)}`}
          completed={totalGemsEarned >= totalPossibleGems}
          className="bg-neutral-950/40"
        />
      </div>

      <hr className="h-px bg-foreground opacity-5 mt-4" />

      {allQuests?.map((quest) => {
        const QuestIcon = getQuestIcon(quest.type, quest.categories ?? []);
        const isCompletedToday = quest.completedToday;
        const hasCompletions =
          quest.refreshPeriodHours !== null &&
          quest.completionCount !== undefined &&
          quest.completionCount > 0;

        return (
          <Fragment key={quest.id}>
            <ListItem
              disabled={isCompletedToday}
              variant="transparent"
              size="md"
              className={cn(
                "[&>div]:items-start hover:bg-white/5 transition-all duration-300",
                isCompletedToday ? "opacity-50" : "cursor-pointer",
              )}
              onClick={() => handleQuestClick(quest)}
            >
              <div className="w-12 h-12 rounded border border-border bg-background/20 backdrop-blur-md flex items-center justify-center">
                <QuestIcon className="w-5 h-5 text-foreground" />
              </div>

              <div className={listItemTextVariants()}>
                <p className="font-medium text-sm text-foreground line-clamp-2">
                  {quest.name}
                </p>
                <div className="text-xs text-muted-foreground leading-4 space-y-2">
                  <p className="sm:text-sm text-xs">{quest.caption}</p>
                  {quest.perpetualRewardsEligible ? (
                    <PerpetualRewardsBadge
                      hasCampaignPoints={quest.campaignPoints > 0}
                    />
                  ) : null}
                  {hasCompletions ? (
                    <p className="flex items-center gap-1">
                      <IterationCcw className="w-3 h-3" />
                      {quest.completionCount} completion
                      {quest.completionCount !== 1 ? "s" : ""}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col items-end gap-1">
                <WeeklyPointsBadge
                  className="self-end"
                  points={quest.weeklyPoints}
                  completed={quest.completed}
                />
                <PointsBadge
                  points={quest.campaignPoints}
                  completed={quest.completed}
                  className="bg-neutral-950/40"
                />
                <GemsBadge
                  gems={quest.gems}
                  completed={quest.completed}
                  className="bg-neutral-950/40"
                />
              </div>
            </ListItem>
            <hr className="h-px bg-foreground opacity-5" />
          </Fragment>
        );
      })}

      <QuestInteractionHandler
        game={gameData}
        completeQuest={handleQuestComplete}
        setInProgressQuest={setInProgressQuest}
        inProgressQuest={inProgressQuest}
      />
    </div>
  );
};
